import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { SectionCard, GradeBadge, EmptyState } from "@/components/common/Primitives";
import { ProgressBadge, StageChip, CtaIcon } from "@/components/common/AuditBadges";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ChevronLeft, ChevronRight, LayoutGrid, List, Building2, User as UserIcon, Calendar } from "lucide-react";
import { TID } from "@/constants/testIds";
import { AUDIT_FILTER_TABS, getAuditDisplayState, filterTabKey } from "@/lib/auditStatus";

const PAGE_SIZE_OPTIONS = [10, 20, 30, 50, 100];

export default function AuditsListPage() {
  const nav = useNavigate();
  const [audits, setAudits] = useState([]);
  const [tab, setTab] = useState("all"); // client-side filter tab (see AUDIT_FILTER_TABS)
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [view, setView] = useState(() => localStorage.getItem("sams_audit_view") || "list");

  useEffect(() => { localStorage.setItem("sams_audit_view", view); }, [view]);

  // Always fetch ALL audits — filtering now happens client-side against a
  // synthesized "tab key" that respects the phase × status matrix.
  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/audits`);
      setAudits(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { setPage(1); }, [tab, pageSize]);

  // Attach a derived tab key to each audit so we filter + count consistently.
  const enriched = useMemo(
    () => audits.map((a) => ({ ...a, _tab: filterTabKey(a), _display: getAuditDisplayState(a) })),
    [audits]
  );

  const filtered = useMemo(
    () => (tab === "all" ? enriched : enriched.filter((a) => a._tab === tab)),
    [enriched, tab]
  );

  const counts = useMemo(() => {
    const c = { all: enriched.length };
    enriched.forEach((a) => { c[a._tab] = (c[a._tab] || 0) + 1; });
    return c;
  }, [enriched]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const pageStart = (currentPage - 1) * pageSize;
  const pageRows = filtered.slice(pageStart, pageStart + pageSize);

  return (
    <div className="space-y-4">
      <div>
        <div className="text-[11px] uppercase tracking-[0.16em] text-slate-500 font-semibold">Execution</div>
        <h1 className="font-heading text-2xl md:text-3xl font-extrabold text-slate-900">Audit Execution</h1>
        <p className="text-sm text-slate-600 mt-1">All audit sessions. Continue in-progress audits or start planned ones.</p>
      </div>

      {/* Status filter tabs — Planned / In Progress / Pending Review / Approved / Closed */}
      <div className="bg-white border border-slate-200 rounded-md p-1 inline-flex gap-1 overflow-x-auto max-w-full">
        {AUDIT_FILTER_TABS.map((t) => {
          const active = tab === t.key;
          const count = counts[t.key] || 0;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`status-tab-${t.key}`}
              className={`px-3 py-1.5 rounded-md text-xs font-semibold whitespace-nowrap transition-colors flex items-center gap-1.5 ${
                active ? "bg-slate-900 text-white" : "text-slate-700 hover:bg-slate-100"
              }`}
            >
              {t.label}
              <span className={`inline-flex items-center justify-center min-w-[18px] h-4 px-1 rounded-sm text-[10px] font-bold ${
                active ? "bg-white/25 text-white" : "bg-slate-200 text-slate-700"
              }`}>{count}</span>
            </button>
          );
        })}
      </div>

      <SectionCard
        title="Audit Sessions"
        subtitle={`${filtered.length} record(s)${tab !== "all" ? ` in ${AUDIT_FILTER_TABS.find((t)=>t.key===tab)?.label}` : ""}`}
        actions={
          <div className="flex items-center gap-3 text-xs">
            {/* View toggle */}
            <div className="inline-flex rounded-md border border-slate-200 overflow-hidden">
              <button
                onClick={() => setView("list")}
                data-testid="audit-view-list"
                title="List view"
                aria-label="List view"
                className={`h-8 w-8 grid place-items-center transition-colors ${view === "list" ? "bg-slate-900 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
              ><List size={14} /></button>
              <button
                onClick={() => setView("card")}
                data-testid="audit-view-card"
                title="Card view"
                aria-label="Card view"
                className={`h-8 w-8 grid place-items-center transition-colors ${view === "card" ? "bg-slate-900 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
              ><LayoutGrid size={14} /></button>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-slate-500">Show</span>
              <Select value={String(pageSize)} onValueChange={(v) => setPageSize(Number(v))}>
                <SelectTrigger data-testid="page-size-selector" className="w-20 h-8">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PAGE_SIZE_OPTIONS.map((n) => (
                    <SelectItem key={n} value={String(n)}>{n}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <span className="text-slate-500">per page</span>
            </div>
          </div>
        }
      >
        {loading ? <div className="text-sm text-slate-500">Loading...</div> :
          filtered.length === 0 ? <EmptyState /> : (
            <>
              {view === "list" ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-slate-100 text-slate-600 text-xs uppercase tracking-wider">
                      <th className="text-left px-3 py-2">Audit #</th>
                      <th className="text-left px-3 py-2">Supplier</th>
                      <th className="text-left px-3 py-2">Plant</th>
                      <th className="text-left px-3 py-2">Date</th>
                      <th className="text-left px-3 py-2">Auditors</th>
                      <th className="text-right px-3 py-2">Score</th>
                      <th className="text-center px-3 py-2">Grade</th>
                      <th className="text-left px-3 py-2">Status</th>
                      <th className="text-right px-3 py-2">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pageRows.map((a) => {
                      const d = a._display;
                      return (
                        <tr key={a.id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                          <td className="px-3 py-2 font-mono text-xs">{a.audit_number}</td>
                          <td className="px-3 py-2 font-semibold text-slate-900">{a.supplier?.name || "—"}</td>
                          <td className="px-3 py-2 text-slate-600">{a.plant?.name || "—"}</td>
                          <td className="px-3 py-2">
                            <div className="font-mono text-xs">{d.date.value || "—"}</div>
                            <div className="text-[10px] text-slate-400 uppercase tracking-wider mt-0.5">{d.date.label}</div>
                          </td>
                          <td className="px-3 py-2 text-xs">{(a.auditor_details || []).map((u) => u.name?.split(" ")[0]).join(", ") || "—"}</td>
                          <td className="px-3 py-2 text-right font-mono font-semibold">{a.percentage ? `${a.percentage}%` : "—"}</td>
                          <td className="px-3 py-2 text-center"><GradeBadge grade={a.grade} /></td>
                          <td className="px-3 py-2">
                            <div className="flex flex-col gap-1 items-start">
                              <ProgressBadge progress={d.progress} testId={`progress-badge-${a.id}`} />
                              <StageChip stage={d.stage} testId={`stage-badge-${a.id}`} />
                              {d.subText && <span className="text-[10px] italic text-slate-500 leading-tight">{d.subText}</span>}
                            </div>
                          </td>
                          <td className="px-3 py-2 text-right">
                            <Button size="sm" data-testid={TID.audit.startBtn(a.id)} onClick={() => nav(`/audits/${a.id}`)} className="h-8 bg-sams-primary hover:bg-sams-primaryHover text-white">
                              <CtaIcon icon={d.cta.icon} className="mr-1" /> {d.cta.label}
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              ) : (
                /* Card view — mobile-responsive grid */
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
                  {pageRows.map((a) => {
                    const d = a._display;
                    return (
                    <div
                      key={a.id}
                      data-testid={`audit-card-${a.id}`}
                      onClick={() => nav(`/audits/${a.id}`)}
                      className="group bg-white border border-slate-200 rounded-md p-4 hover:border-sams-primary hover:shadow-sm transition-all cursor-pointer flex flex-col gap-3"
                    >
                      {/* Header row */}
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="font-mono text-[11px] text-slate-500 uppercase tracking-wider">{a.audit_number}</div>
                          <div className="font-heading font-bold text-slate-900 mt-0.5 truncate">{a.supplier?.name || "—"}</div>
                        </div>
                        <div className="flex flex-col items-end gap-1 shrink-0">
                          <ProgressBadge progress={d.progress} testId={`progress-badge-${a.id}`} />
                          <StageChip stage={d.stage} testId={`stage-badge-${a.id}`} />
                        </div>
                      </div>

                      {d.subText && (
                        <div data-testid={`audit-subtext-${a.id}`} className="text-[11px] italic text-slate-500 -mt-1">{d.subText}</div>
                      )}

                      {/* Meta rows */}
                      <div className="space-y-1.5 text-xs">
                        <div className="flex items-center gap-2 text-slate-600">
                          <Building2 size={12} className="text-slate-400 shrink-0" />
                          <span className="truncate">{a.plant?.name || "—"}</span>
                        </div>
                        <div className="flex items-center gap-2 text-slate-600">
                          <Calendar size={12} className="text-slate-400 shrink-0" />
                          <span className="font-mono">{d.date.value || "—"}</span>
                          <span className="text-[10px] text-slate-400 uppercase tracking-wider">· {d.date.label}</span>
                        </div>
                        <div className="flex items-center gap-2 text-slate-600">
                          <UserIcon size={12} className="text-slate-400 shrink-0" />
                          <span className="truncate">{(a.auditor_details || []).map((u) => u.name?.split(" ")[0]).join(", ") || "Unassigned"}</span>
                        </div>
                      </div>

                      {/* Score + Grade + CTA */}
                      <div className="flex items-center justify-between pt-3 border-t border-slate-100">
                        <div>
                          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Score</div>
                          <div className="font-heading text-lg font-black text-slate-900 leading-none mt-0.5">
                            {a.percentage ? `${a.percentage}%` : <span className="text-slate-400">—</span>}
                          </div>
                        </div>
                        <div className="text-center">
                          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Grade</div>
                          <GradeBadge grade={a.grade} />
                        </div>
                        <Button
                          size="sm"
                          data-testid={TID.audit.startBtn(a.id)}
                          onClick={(e) => { e.stopPropagation(); nav(`/audits/${a.id}`); }}
                          className="h-8 bg-sams-primary hover:bg-sams-primaryHover text-white"
                        >
                          <CtaIcon icon={d.cta.icon} className="mr-1" /> {d.cta.label}
                        </Button>
                      </div>
                    </div>
                    );
                  })}
                </div>
              )}

              {/* Pagination footer */}
              <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-200">
                <div className="text-xs text-slate-500">
                  Showing <span className="font-semibold text-slate-900">{pageStart + 1}</span>–
                  <span className="font-semibold text-slate-900">{Math.min(pageStart + pageSize, audits.length)}</span>
                  {" of "}<span className="font-semibold text-slate-900">{audits.length}</span>
                  {" · Page "}<span className="font-semibold text-slate-900">{currentPage}</span>{" of "}
                  <span className="font-semibold text-slate-900">{totalPages}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Button size="sm" variant="outline" onClick={() => setPage(1)} disabled={currentPage === 1}
                    data-testid="audit-pagination-first" className="h-8 px-2 text-xs">« First</Button>
                  <Button size="sm" variant="outline" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={currentPage === 1}
                    data-testid="audit-pagination-prev" className="h-8 px-2">
                    <ChevronLeft size={13} />
                  </Button>
                  {(() => {
                    const pages = [];
                    const start = Math.max(1, currentPage - 2);
                    const end = Math.min(totalPages, start + 4);
                    for (let i = start; i <= end; i++) pages.push(i);
                    return pages.map((n) => (
                      <button key={n} onClick={() => setPage(n)} data-testid={`audit-pagination-page-${n}`}
                        className={`h-8 min-w-[32px] px-2 text-xs rounded-md border transition-colors ${
                          n === currentPage
                            ? "bg-sams-primary border-sams-primary text-white font-semibold"
                            : "border-slate-200 text-slate-700 hover:bg-slate-50"
                        }`}>{n}</button>
                    ));
                  })()}
                  <Button size="sm" variant="outline" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}
                    data-testid="audit-pagination-next" className="h-8 px-2">
                    <ChevronRight size={13} />
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setPage(totalPages)} disabled={currentPage === totalPages}
                    data-testid="audit-pagination-last" className="h-8 px-2 text-xs">Last »</Button>
                </div>
              </div>
            </>
          )
        }
      </SectionCard>
    </div>
  );
}
